import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Spinner, Alert } from 'reactstrap';
import { PhotoUploadState } from '../../constants/PhotoUploadState';
import CommonDeleteIconButton from '../../common/components/CommonDeleteIconButton';
import CommonDropzone from '../../common/components/CommonDropzone/CommonDropzone';

function DamagePhotoUpload({
  photo,
  onPhotoDrop,
  onPhotoDelete,
  isUploading = false,
  uploadError = null,
  className = '',
  minHeight = '150px',
  dropzoneText = 'Click to browse or drag & drop photos here',
  loadingText = 'Uploading photo...',
  retryText = 'Click to retry upload',
  deleteButtonAriaLabel = 'Delete photo',
  previewAltText = 'Damage preview',
  acceptedFileTypes = 'image/*',
  onRetry,
}) {
  const [previewError, _setPreviewError] = useState(null);

  const containerClassName = useMemo(
    () => `damage-photo-upload ${className}`.trim(),
    [className],
  );

  let currentUploadState;
  if (isUploading) {
    currentUploadState = PhotoUploadState.IN_PROGRESS;
  } else if (uploadError) {
    currentUploadState = PhotoUploadState.ERROR;
  } else if (photo) {
    currentUploadState = PhotoUploadState.SENT;
  } else {
    currentUploadState = PhotoUploadState.EMPTY;
  }

  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (acceptedFiles.length === 0) return;
      await onPhotoDrop(acceptedFiles[0]);
    },
    [onPhotoDrop],
  );

  const handleDelete = useCallback(() => {
    onPhotoDelete();
  }, [onPhotoDelete]);

  const handleRetry = useCallback(() => {
    if (onRetry) {
      onRetry();
    }
  }, [onRetry]);

  if (currentUploadState === PhotoUploadState.IN_PROGRESS) {
    return (
      <div
        className={`d-flex justify-content-center align-items-center ${containerClassName}`}
        style={{ minHeight }}
        role="status"
        aria-live="polite"
        aria-label={loadingText}
      >
        <div className="text-center">
          <Spinner className="mb-2" />
          <div className="small text-muted">{loadingText}</div>
        </div>
      </div>
    );
  }

  if (photo?.status === 'PENDING') {
    return (
      <div
        className={`d-flex justify-content-center align-items-center ${className}`}
        style={{ minHeight }}
      >
        <Spinner />
      </div>
    );
  }

  if (currentUploadState === PhotoUploadState.ERROR) {
    return (
      <div
        className={`damage-photo-error ${containerClassName}`}
        style={{ minHeight }}
        role="alert"
        aria-live="assertive"
      >
        <Alert color="danger" className="mb-0">
          <div className="text-center">
            <i className="fa fa-exclamation-triangle mb-2" aria-hidden="true" />
            <div className="small mb-2">{uploadError}</div>
            {onRetry && (
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={handleRetry}
                aria-label="Retry photo upload"
              >
                {retryText}
              </button>
            )}
          </div>
        </Alert>
      </div>
    );
  }

  if (photo?.fileUrl && currentUploadState === PhotoUploadState.SENT) {
    const displayUrl = photo.thumbnailUrl ? photo.thumbnailUrl : photo.fileUrl;
    return (
      <div
        className={`added-photo-container ${containerClassName}`}
        role="img"
        aria-label={`${previewAltText} - uploaded successfully`}
      >
        <img alt={previewAltText} src={displayUrl} className="added-photo" loading="lazy" />

        <CommonDeleteIconButton
          className="delete-photo__button"
          handleClick={handleDelete}
          ariaLabel={deleteButtonAriaLabel}
          title={deleteButtonAriaLabel}
        />
        {previewError && <div className="small text-danger mt-2">{previewError}</div>}
      </div>
    );
  }

  return (
    <div className={containerClassName}>
      <CommonDropzone
        onDrop={onDrop}
        accept={acceptedFileTypes}
        multiple={false}
        dropzoneText={dropzoneText}
        containerStyles={{ minHeight }}
        ariaLabel="Drop zone for damage photo upload"
        ariaDescribedBy="dropzone-instructions"
      />
      <div id="dropzone-instructions" className="visually-hidden">
        {dropzoneText}. Accepted file types: images.
      </div>
    </div>
  );
}

DamagePhotoUpload.propTypes = {
  photo: PropTypes.shape({
    fileUrl: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
  }),
  onPhotoDrop: PropTypes.func.isRequired,
  onPhotoDelete: PropTypes.func.isRequired,
  isUploading: PropTypes.bool,
  uploadError: PropTypes.string,
  className: PropTypes.string,
  minHeight: PropTypes.string,
  dropzoneText: PropTypes.string,
  loadingText: PropTypes.string,
  retryText: PropTypes.string,
  deleteButtonAriaLabel: PropTypes.string,
  previewAltText: PropTypes.string,
  acceptedFileTypes: PropTypes.string,
  onRetry: PropTypes.func,
};

DamagePhotoUpload.defaultProps = {
  photo: null,
  isUploading: false,
  uploadError: null,
  className: '',
  minHeight: '150px',
  dropzoneText: 'Click to browse or drag & drop photos here',
  loadingText: 'Uploading photo...',
  retryText: 'Click to retry upload',
  deleteButtonAriaLabel: 'Delete photo',
  previewAltText: 'Damage preview',
  acceptedFileTypes: 'image/*',
  onRetry: null,
};

export default DamagePhotoUpload;
